// Settings → Notifications (B11): this phone, for whoever is signed in. Permission is asked only
// when the switch is tapped. The parent also sets the family's quiet hours here.
import { useEffect, useState } from 'react'
import type { Person } from '../../shared/events'
import { useToast } from '../app/toast'
import { pushStatus, pushSupported, setQuietHours, turnOffPush, turnOnPush, type PushStatus } from '../data/push'
import { t } from '../strings/en'
import { useSession } from '../data/session'
import { isSolo } from '../domain/players'

export function NotifyPanel({ person }: { person: Person }) {
  const [status, setStatus] = useState<PushStatus | null>(null)
  const [busy, setBusy] = useState(false)
  const solo = useSession((s) => isSolo(s.people))
  const supported = pushSupported()
  useEffect(() => {
    void pushStatus()
      .then(setStatus)
      .catch(() => setStatus(null))
  }, [person.id])
  if (!status) return null // offline: nothing to change until there's signal

  const toggle = async () => {
    setBusy(true)
    try {
      if (status.on) {
        await turnOffPush()
        setStatus({ ...status, on: false })
      } else {
        const r = await turnOnPush()
        if (r === 'on') setStatus({ ...status, on: true })
        else useToast.getState().show(t.notify.failed[r] ?? t.notify.unsupported, 4000)
      }
    } catch {
      useToast.getState().show(t.notify.offline, 3000)
    } finally {
      setBusy(false)
    }
  }
  const quiet = (field: 'start' | 'end', value: string) => {
    if (!/^\d{2}:\d{2}$/.test(value)) return
    const q = { ...status.quiet, [field]: value }
    setStatus({ ...status, quiet: q })
    void setQuietHours(q).then(
      () => useToast.getState().show(t.notify.quietSaved(q.start, q.end), 2500),
      () => useToast.getState().show(t.notify.offline, 3000),
    )
  }

  // Until the server can push (its VAPID keys are set) the switch would send nothing: "Coming
  // soon" instead, so nobody turns it on and waits. A phone already subscribed keeps its switch off.
  const soon = status.ready !== true && !status.on
  return (
    <div className="panel sheet-panel notify-panel">
      <div className="row">
        <div>
          <b>{t.notify.title}</b>
          <small>{!supported ? t.notify.unsupported : soon ? t.notify.soonSub : person.kind === 'child' ? t.notify.childSub : solo ? t.notify.soloSub : t.notify.grownUpSub}</small>
        </div>
        {supported && soon ? <span className="tag blue">{t.notify.soon}</span> : null}
        {supported && !soon ? <button className="toggle" role="switch" aria-checked={status.on} aria-label={t.notify.title} disabled={busy} onClick={() => void toggle()} /> : null}
      </div>
      {person.kind === 'admin' && !soon ? (
        <div className="row">
          <div>
            <b>{t.notify.quiet}</b>
            <small>{t.notify.quietSub}</small>
          </div>
          <span className="quiet-times">
            <input type="time" className="date-in" aria-label={t.notify.quietFrom} value={status.quiet.start} onChange={(e) => quiet('start', e.target.value)} />
            <input type="time" className="date-in" aria-label={t.notify.quietTo} value={status.quiet.end} onChange={(e) => quiet('end', e.target.value)} />
          </span>
        </div>
      ) : null}
    </div>
  )
}
